import { DepressionContentPage } from "@/components/DepressionContentPage";
import type { FAQItem } from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";

const faqs: FAQItem[] = [
  {
    question: "How long do antidepressants usually take to work?",
    answer:
      "Antidepressants may take several weeks to reach full effect. An adequate trial is generally considered to be a sufficient duration at a generally accepted dose, which is one reason clinicians look at treatment history carefully.",
  },
  {
    question: "What is a partial response to an antidepressant?",
    answer:
      "A partial response means some improvement, but symptoms remain. This is one factor clinicians consider when discussing whether additional treatment options may be appropriate.",
  },
  {
    question: "What if side effects make medication difficult?",
    answer:
      "Difficulty tolerating medication is a real factor in treatment planning. Side effects and tolerability should be discussed with your prescribing provider, who can help weigh options.",
  },
  {
    question: "Should I stop medication if I am interested in TMS?",
    answer:
      "No. Do not stop or change prescribed psychiatric medication without speaking with your prescribing healthcare provider. Medication decisions are clinical decisions made with your prescriber.",
  },
  {
    question: "What is treatment-resistant depression?",
    answer:
      "Treatment-resistant depression generally refers to depression that has not responded adequately to one or more adequate antidepressant trials. It is a clinical category describing the treatments, not a verdict on the person.",
  },
  {
    question: "Is TMS medication-free?",
    answer:
      "TMS is a non-invasive treatment that uses magnetic stimulation rather than medication. It does not involve anesthesia for standard treatment. Whether it is appropriate is determined through clinical evaluation.",
  },
  {
    question: "Does insurance cover TMS?",
    answer:
      "Many commercial insurance plans cover TMS for eligible patients when medical necessity criteria are met. Coverage varies by plan and is not guaranteed. Our team can help verify your benefits.",
  },
];

export default function BeyondAntidepressants() {
  return (
    <DepressionContentPage
      seo={{
        title: "When Antidepressants Aren't Enough",
        description:
          "Still experiencing depression despite treatment? Learn what clinicians consider when medication has not provided enough relief.",
        canonical: "/when-antidepressants-are-not-enough",
        keywords:
          "antidepressants not working, treatment-resistant depression, depression medication not enough, TMS after antidepressants, next treatment options",
      }}
      badge="Mental Health Education"
      heroTitle="What If Depression Medication Isn't Giving You Enough Relief?"
      heroSubtitle="When symptoms continue, the next step is not necessarily “just try another medication.” A broader treatment conversation may be appropriate."
      heroCtaPrimary="Check My Insurance Options"
      heroCtaPrimaryHref="/check-my-insurance-options"
      sections={[
        {
          heading:
            "There Are Different Reasons Medication May Not Feel Successful",
          variant: "text",
          body: (
            <>
              <p>Several factors can affect how medication works, including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  Side effects that make a full dose difficult to tolerate
                </li>
                <li>Inadequate response despite an adequate trial</li>
                <li>Partial response with lingering symptoms</li>
                <li>Difficulty tolerating medication</li>
                <li>Not having completed an adequate treatment trial</li>
                <li>Other symptoms complicating the picture</li>
              </ul>
              <p>
                None of this means you have not tried hard enough. It means the
                treatment conversation may benefit from broadening.
              </p>
            </>
          ),
        },
        {
          heading: "Medication Decisions Should Be Made With Your Prescriber",
          variant: "callout",
          callout:
            "Do not stop or change prescribed psychiatric medication without speaking with your prescribing healthcare provider. Adherence and medication changes are clinical decisions made together with your prescriber.",
        },
        {
          heading: "When Additional Treatment Options May Be Discussed",
          variant: "text",
          body: (
            <>
              <p>Depending on the individual, options may include:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Psychotherapy</li>
                <li>
                  Medication adjustments through your prescribing provider
                </li>
                <li>TMS</li>
                <li>Accelerated TMS for appropriate candidates</li>
                <li>Other clinician-recommended options</li>
              </ul>
            </>
          ),
        },
        {
          heading: "What Is Treatment-Resistant Depression?",
          variant: "text",
          body: (
            <p>
              Treatment-resistant depression generally refers to depression that
              has not responded adequately to one or more adequate
              antidepressant trials. It is a clinical category describing the
              treatments, not a verdict on the person experiencing them.
            </p>
          ),
        },
        {
          heading: "Where TMS Fits Into Depression Care",
          variant: "text",
          body: (
            <>
              <p>
                TMS is an FDA-cleared, non-invasive treatment that may be
                considered for eligible patients after clinical evaluation,
                often when depression has not responded adequately to prior
                treatment.
              </p>
              <p>
                <Link
                  to="/services/tms"
                  className="text-accent font-semibold hover:underline underline-offset-2"
                >
                  Learn about TMS therapy.
                </Link>{" "}
                &nbsp;·&nbsp;{" "}
                <Link
                  to="/saint"
                  className="text-accent font-semibold hover:underline underline-offset-2"
                >
                  Learn about accelerated TMS / SAINT.
                </Link>{" "}
                &nbsp;·&nbsp;{" "}
                <Link
                  to="/check-my-insurance-options"
                  className="text-accent font-semibold hover:underline underline-offset-2"
                >
                  Check your insurance options.
                </Link>
              </p>
            </>
          ),
        },
      ]}
      faq={faqs}
      faqHeading="Frequently Asked Questions"
      relatedLinks={[
        { title: "Standard TMS Therapy", href: "/services/tms" },
        { title: "SAINT® Accelerated TMS", href: "/saint" },
        {
          title: "Am I a Candidate for TMS?",
          href: "/am-i-a-candidate",
        },
        {
          title: "Check My Insurance Options",
          href: "/check-my-insurance-options",
        },
        {
          title: "Explore More Mental Health Education",
          href: "/mental-health-resources",
        },
      ]}
      finalCtaHeading="The Next Step Isn't Necessarily Just Another Medication"
      finalCtaText="If medication hasn't provided enough relief, a broader treatment conversation can help you understand what options may be appropriate."
      finalCtaPrimaryLabel="Check My Insurance Options"
      finalCtaPrimaryHref="/check-my-insurance-options"
      finalCtaSecondaryLabel="Schedule a Private Consultation"
      finalCtaSecondaryHref="/contactus"
    />
  );
}
